import React from "react";
import { Card, Badge, Separator, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@hotelise/ui";
import { Percent, CreditCard, Wrench, SprayCan, StickyNote, Building2, ArrowDownLeft, ArrowUpRight, Info } from "lucide-react";
import { VatDisplayMode } from "interfaces/contracts";
import type { Contract, ChannelKey, MarkupAppliedTo } from "interfaces/contracts";
import { computeFeeBreakdown, hasVatRate, headlineFee, resolveVatDisplayMode, showsFeeVat } from "./vatDisplay";

/* ── Channel metadata ─────────────────────────────────────── */

type DisplayChannelKey = "airbnb" | "booking" | "vrbo" | "direct";

const CHANNEL_META: Record<DisplayChannelKey, { label: string; icon: React.ReactNode; color: string }> = {
	airbnb: { label: "Airbnb", icon: <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.6 0 12 0zm5.7 17.6c-.3.6-.9 1-1.5 1-.3 0-.5-.1-.8-.2-1.5-.8-2.7-2.1-3.6-3.3-.3-.4-.5-.7-.7-1.1-.2.4-.5.7-.7 1.1-.9 1.2-2.1 2.5-3.6 3.3-.3.1-.5.2-.8.2-.6 0-1.2-.4-1.5-1-.4-.8-.3-1.8.2-3 .4-.9 1-1.8 1.7-2.7.8-1 1.7-2 2.6-2.8.4-.4.9-.8 1.3-1.1.1-.1.3-.2.4-.3.1-.1.3-.2.4-.2.2-.1.3-.2.5-.2s.3.1.5.2c.1.1.3.1.4.2.1.1.3.2.4.3.4.3.9.7 1.3 1.1.9.9 1.8 1.8 2.6 2.8.7.9 1.3 1.8 1.7 2.7.5 1.2.6 2.2.2 3z" /></svg>, color: "text-[#FF5A5F]" },
	booking: { label: "Booking.com", icon: <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.6 0 12 0zm-1.2 17.1H8.4v-1.3c-.7.9-1.7 1.5-3 1.5-2.3 0-3.9-1.8-3.9-4.2s1.6-4.2 3.9-4.2c1.3 0 2.3.6 3 1.5V6.9h2.4v10.2zM7.5 11c-1 0-1.8.8-1.8 2.1s.8 2.1 1.8 2.1 1.8-.8 1.8-2.1S8.5 11 7.5 11zm14 6.1h-2.4v-1.3c-.7.9-1.7 1.5-3 1.5-2.3 0-3.9-1.8-3.9-4.2s1.6-4.2 3.9-4.2c1.3 0 2.3.6 3 1.5V6.9h2.4v10.2zm-3.9-6.1c-1 0-1.8.8-1.8 2.1s.8 2.1 1.8 2.1 1.8-.8 1.8-2.1-.8-2.1-1.8-2.1z" /></svg>, color: "text-[#003580]" },
	direct: { label: "Direct", icon: <Building2 className="h-5 w-5" aria-hidden="true" />, color: "text-primary" },
	vrbo: { label: "Vrbo", icon: <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.6 0 12 0zm6.5 8.5l-3.2 8.2c-.2.5-.7.8-1.2.8s-1-.3-1.2-.8L12 14l-.9 2.7c-.2.5-.7.8-1.2.8s-1-.3-1.2-.8L5.5 8.5c-.1-.3 0-.6.2-.8.2-.2.5-.2.8-.1l3.3 1.6c.3.1.5.4.5.7l.7 2.1.7-2.1c.2-.5.7-.8 1.2-.8s1 .3 1.2.8l.7 2.1.7-2.1c.1-.3.3-.6.5-.7l3.3-1.6c.3-.1.6-.1.8.1.2.2.3.5.2.8z" /></svg>, color: "text-[#3B5998]" },
};

const APPLIED_TO_LABEL: Record<MarkupAppliedTo, string> = {
	total: "Total",
	channel_fee: "Channel Fee",
	cleaning: "Cleaning",
};

const currencySymbol: Record<string, string> = { USD: "$", AMD: "\u058F", GBP: "\u00A3", EUR: "\u20AC" };

/* ── Props ────────────────────────────────────────────────── */

interface ContractVisualProps {
	contract: Contract;
	className?: string;
}

/* ── Component ────────────────────────────────────────────── */

export default function ContractVisual({ contract, className }: ContractVisualProps) {
	const storedFee = contract.hoteliseFee ?? 0;
	const channels: DisplayChannelKey[] = ["airbnb", "booking", "vrbo", "direct"];
	const showVat = showsFeeVat(contract);
	const vat = contract.vat ?? 0;
	const vatMode = resolveVatDisplayMode(contract);
	const showFeePlusVat = showVat && vatMode === VatDisplayMode.FEE_PLUS_VAT;
	const showFeeIncludesVat = showVat && vatMode === VatDisplayMode.FEE_INCLUDES_VAT;
	const feeBreakdown = showFeeIncludesVat ? computeFeeBreakdown(storedFee, vat) : null;
	const grossFee = headlineFee(contract);

	return (
		<div className={className}>
			{/* ── Fee Section ──────────────────────────────── */}
			<section className="space-y-3">
				<div className="flex items-center gap-2">
					<div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
						<Percent className="h-4 w-4 text-primary" />
					</div>
					<h3 className="font-semibold text-base">Management Fee</h3>
				</div>

				<Card className="p-4">
					<div className="flex items-baseline gap-2">
						<span className="text-3xl font-bold tabular-nums">{grossFee}%</span>
						{showFeePlusVat && (
							<span className="text-xs text-muted-foreground">
								+ {vat}% VAT
							</span>
						)}
						{feeBreakdown && (
							<div className="flex flex-col">
								<span className="text-xs text-muted-foreground">incl. VAT {vat}%</span>
								<span className="text-xs text-muted-foreground tabular-nums">
									Net {feeBreakdown.net}% + {feeBreakdown.vatShare}% VAT
								</span>
							</div>
						)}
					</div>
					<p className="text-sm text-muted-foreground mt-2">
						This is the service fee Hotelise charges for managing your property.
					</p>
				</Card>

				{hasVatRate(contract) && (
					<div className="flex items-start gap-2.5 p-3 rounded-lg bg-amber-500/15 border border-amber-500/30">
						<Info className="h-4 w-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
						<div className="space-y-1.5">
							<span className="text-xs font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300">Important</span>
							<p className="text-xs text-amber-700/90 dark:text-amber-300/90 leading-relaxed">
								When Hotelise is charging VAT on its services, channels are not charging VAT to Hotelise when providing their services. Therefore, VAT is effectively transferred from the channel to Hotelise.
							</p>
							<p className="text-[11px] text-muted-foreground leading-relaxed italic">
								In some countries, such as Armenia, Hotelise assumes the liability of becoming a tax agent for electronic services provided to it, and is therefore liable to pay the corresponding VAT on behalf of the channel.
							</p>
						</div>
					</div>
				)}
			</section>

			<Separator className="my-5" />

			{/* ── Channels Section ─────────────────────────── */}
			<section className="space-y-3">
				<div className="flex items-center gap-2">
					<div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
						<CreditCard className="h-4 w-4 text-primary" />
					</div>
					<h3 className="font-semibold text-base">Booking Channels & Payouts</h3>
				</div>

				<div className="space-y-3">
					{channels.map((ch) => {
						const meta = CHANNEL_META[ch];
						// channelSplit: 0 = owner receives payout, 1 = hotelise manages
						const splitValue = contract.channelSplit?.[ch];
						const isOwner = splitValue === 0;
						const markups = contract.channelMarkups?.[ch as ChannelKey] || [];

						return (
							<Card key={ch} className="p-4">
								<div className="flex items-start gap-3">
									<div className={`shrink-0 mt-0.5 ${meta.color}`}>
										{meta.icon}
									</div>
									<div className="flex-1 min-w-0 space-y-2">
										<div className="flex items-center justify-between gap-2">
											<span className="font-medium text-sm">{meta.label}</span>
											{isOwner ? (
												<Badge variant="outline" className="gap-1 text-xs shrink-0">
													<ArrowDownLeft className="h-3 w-3 text-emerald-500" />
													Direct to you
												</Badge>
											) : (
												<Badge variant="secondary" className="gap-1 text-xs shrink-0">
													<ArrowUpRight className="h-3 w-3 text-amber-500" />
													Managed
												</Badge>
											)}
										</div>

										<p className="text-xs text-muted-foreground leading-relaxed">
											{isOwner
												? "You will receive payouts from this channel directly into your account."
												: "We collect payouts on your behalf. This may include payment infrastructure and processing."}
										</p>

										{/* Markups */}
										{markups.length > 0 && (
											<div className="pt-1 space-y-1">
												<span className="text-xs font-medium text-muted-foreground">Markups applied:</span>
												<TooltipProvider delayDuration={200}>
													<div className="flex flex-wrap gap-1.5">
														{markups.map((m) => (
															<Tooltip key={m.id}>
																<TooltipTrigger asChild>
																	<Badge variant="outline" className="text-[11px] tabular-nums cursor-help">
																		{m.name} {m.percent}%
																	</Badge>
																</TooltipTrigger>
																<TooltipContent side="bottom" className="text-xs">
																	Applied on {APPLIED_TO_LABEL[m.appliedTo]}
																</TooltipContent>
															</Tooltip>
														))}
													</div>
												</TooltipProvider>
											</div>
										)}
									</div>
								</div>
							</Card>
						);
					})}
				</div>
			</section>

			<Separator className="my-5" />

			{/* ── Conditions ────────────────────────────────── */}
			<section className="space-y-3">
				<div className="flex items-center gap-2">
					<div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
						<Info className="h-4 w-4 text-primary" />
					</div>
					<h3 className="font-semibold text-base">Conditions</h3>
				</div>

				<div className="space-y-3">
					{/* Cleaning */}
					<Card className="p-4">
						<div className="flex items-start gap-3">
							<div className="h-8 w-8 rounded-lg bg-accent flex items-center justify-center shrink-0">
								<SprayCan className="h-4 w-4 text-accent-foreground" />
							</div>
							<div className="space-y-1">
								<span className="font-medium text-sm">Cleaning Operations</span>
								{contract.doNotClaimCleaningCost ? (
									<p className="text-xs text-muted-foreground leading-relaxed">
										You are organising cleaning operations independently. Cleaning fees from reservations will come directly to you.
									</p>
								) : (
									<p className="text-xs text-muted-foreground leading-relaxed">
										We organise cleaning operations for your property. All cleaning fees from reservations are collected by Hotelise.
									</p>
								)}
							</div>
						</div>
					</Card>

					{/* Maintenance */}
					<Card className="p-4">
						<div className="flex items-start gap-3">
							<div className="h-8 w-8 rounded-lg bg-accent flex items-center justify-center shrink-0">
								<Wrench className="h-4 w-4 text-accent-foreground" />
							</div>
							<div className="space-y-1">
								<div className="flex items-center gap-2">
									<span className="font-medium text-sm">Maintenance Subscription</span>
									{contract.subscriptions?.maintenance?.enabled ? (
										<Badge variant="default" className="text-[11px]">Active</Badge>
									) : (
										<Badge variant="secondary" className="text-[11px]">Inactive</Badge>
									)}
								</div>
								{(() => {
									const maintenance = contract.subscriptions?.maintenance;
									return maintenance?.enabled ? (
									<>
										<p className="text-xs text-muted-foreground leading-relaxed">
											Our engineers are available to respond to issues, attempt fixes, or organise repairs when things break in your property.
										</p>
										<p className="text-sm font-medium tabular-nums mt-1">
											{currencySymbol[maintenance.currency] ?? ""}
											{maintenance.amount.toLocaleString()}/
											{maintenance.frequency}
										</p>
									</>
								) : (
									<p className="text-xs text-muted-foreground leading-relaxed">
										No active maintenance subscription. You handle property maintenance independently.
									</p>
								);
								})()}
							</div>
						</div>
					</Card>

					{/* Notes */}
					{contract.notes && (
						<Card className="p-4">
							<div className="flex items-start gap-3">
								<div className="h-8 w-8 rounded-lg bg-accent flex items-center justify-center shrink-0">
									<StickyNote className="h-4 w-4 text-accent-foreground" />
								</div>
								<div className="space-y-1">
									<span className="font-medium text-sm">Additional Notes</span>
									<p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-wrap">{contract.notes}</p>
								</div>
							</div>
						</Card>
					)}
				</div>
			</section>
		</div>
	);
}
